import type { QueryFunctionContext } from '@tanstack/react-query';
import type { InferType } from 'yup';

import bookingInstance from '@/lib/axios/booking-instance';

import type { IBaseResponse } from '@/types/base-response';
import type { IBranch, IRole, IUser } from './user-access.types';
import type {
  employeeManagementSchema,
  locationSchema,
} from './user-access.validation';
import { AxiosError } from 'axios';
import { handleRequest } from '@/lib/api/factories/base-api-factory';
import type { IProfile } from '@/types/profile';

const getListUser = async ({
  queryKey,
}: QueryFunctionContext): Promise<IBaseResponse<IUser[]>> => {
  const response = await bookingInstance.get('/user', { params: queryKey[1] });

  return response.data;
};

const getUserById = async ({
  queryKey,
}: QueryFunctionContext): Promise<IBaseResponse<IUser>> => {
  const response = await bookingInstance.get(`/user/${queryKey[1]}`);

  return response.data;
};

const createUser = async (data: InferType<typeof employeeManagementSchema>) => {
  try {
    const response = await bookingInstance.post('/user/create', data);

    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error.response?.data;
    }
  }
};

const editUser = async ({
  id,
  data,
}: {
  id: string;
  data: InferType<typeof employeeManagementSchema>;
}) => {
  try {
    const response = await bookingInstance.put(`/user/${id}`, data);

    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error.response?.data;
    }
  }
};

const getListRole = async (): Promise<IBaseResponse<IRole[]>> => {
  const response = await bookingInstance.get('/role');

  return response.data;
};

const getListLocation = async ({
  queryKey,
}: QueryFunctionContext): Promise<IBaseResponse<IBranch[]>> => {
  const response = await bookingInstance.get('/branches', {
    params: queryKey[1],
  });

  return response.data;
};

const getLocationById = async ({
  queryKey,
}: QueryFunctionContext): Promise<IBaseResponse<IBranch>> => {
  const response = await bookingInstance.get(`/branches/${queryKey[1]}`);

  return response.data;
};

const createLocation = async (data: InferType<typeof locationSchema>) => {
  try {
    const response = await bookingInstance.post('/branches', data);

    return response.data;
  } catch (err) {
    if (err instanceof AxiosError) {
      throw err.response?.data;
    }

    throw err;
  }
};

const editLocation = async ({
  id,
  data,
}: {
  id: string;
  data: InferType<typeof locationSchema>;
}) => {
  try {
    const response = await bookingInstance.put(`/branches/${id}`, data);

    return response.data;
  } catch (err) {
    if (err instanceof AxiosError) {
      throw err.response?.data;
    }

    throw err;
  }
};

const getProfile = () => {
  return handleRequest<IBaseResponse<IProfile>>(
    bookingInstance.get('/user/profile/me')
  );
};

export const UserAccessService = {
  getListUser,
  getUserById,
  createUser,
  editUser,
  getListRole,
  getListLocation,
  getLocationById,
  createLocation,
  editLocation,
  getProfile,
};
